import React from 'react';
import {
  Search,
  BarChart3,
  Award,
  Calendar,
  BookOpen,
  Briefcase,
  BriefcaseBusiness,
  Medal,
  Trophy,
  Target,
  ThumbsUp
} from 'lucide-react';

type TeacherStudentProps = {
  filteredStudents: any[];
  selectedOrganization: string;
  setSelectedOrganization: (org: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  organizations: any[];
  getStatusColor: (status: string) => string;
  setSelectedStudent: (student: any) => void;
  setDeepLinkAssignment: (student: any) => void;
  setShowOrganizationJobAssignments: (show: boolean) => void;
  setShowOrganizationJobs: (show: boolean) => void;
};

const TeacherStudent: React.FC<TeacherStudentProps> = ({
  filteredStudents,
  selectedOrganization,
  setSelectedOrganization,
  searchTerm,
  setSearchTerm,
  organizations,
  getStatusColor,
  setSelectedStudent,
  setDeepLinkAssignment,
  setShowOrganizationJobAssignments,
  setShowOrganizationJobs,
}) => {
  return (
    <>
      {/* Actions Bar */}
      <div className="dashboard-card p-4 mb-8">
        {/* Organization Filter - Horizontal List */}
        <div hidden className="mb-4 pb-4 border-b border-gray-100">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedOrganization("<ALL>")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedOrganization === "<ALL>"
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Classes
            </button>
            {organizations && organizations.map((org: any) => (
              <button
                key={org.organizationId}
                onClick={() => setSelectedOrganization(org.organizationName)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedOrganization === org.organizationName
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {org.organizationName}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-4 w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white/50 border border-gray-200 rounded-lg w-full focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Student List */}
        <div className="lg:col-span-2 space-y-6">
          <div className="dashboard-card overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">Students</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {searchTerm && searchTerm.trim().length > 0 ? (
                filteredStudents.length > 0 ? (
                  filteredStudents.map((student: any, index: number) => {
                    const status = student.status || 'active';
                    return (
                      <div
                        key={`main-${student.id}-${index}`}
                        className="p-4 hover:bg-indigo-50/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4"
                            onClick={() => setSelectedStudent(student)}
                          >
                            <img
                              src={student.avatar}
                              alt={student.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div>
                              <h3 className="font-medium text-gray-800">{student.name}</h3>
                              <p className="text-sm text-gray-500">
                                {student.grade} • {student.school}
                              </p>
                              <p className="text-sm text-gray-500">{student.teacher}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="hidden sm:block text-right">
                              <p className="text-sm font-medium text-gray-800">{student.points} pts</p>
                              {/* <p className="text-xs text-gray-500">Last active: {new Date(student.lastActive).toLocaleString()}</p> */}
                            </div>
                            {student.jobAssignment && student.jobAssignment.length > 0 && (
                              <button
                                className="p-2 bg-indigo-500 text-white rounded-lg flex items-center gap-2"
                                onClick={() => {
                                  setDeepLinkAssignment(student);
                                  setShowOrganizationJobAssignments(true);
                                }}
                              >
                                <BriefcaseBusiness className="w-4 h-4" />
                              </button>
                            )}
                            <div className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1 ${getStatusColor(status)}`}>
                              {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-6 text-center text-gray-500">No students found matching "{searchTerm}"</div>
                )
              ) : (
                <div className="p-6 text-center text-gray-500">Enter at least 1 character to show matching students</div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div hidden className="dashboard-card p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-4">
              <button className="p-4 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors text-center">
                <BarChart3 className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Reports</span>
              </button>
              <button className="p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors text-center">
                <Award className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Awards</span>
              </button>
              <button className="p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors text-center">
                <Calendar className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Schedule</span>
              </button>
              {import.meta.env.VITE_ACORN_EDITION === 'full' && (
              <button className="p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors text-center">
                <BookOpen className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Lessons</span>
              </button>
              )}
              <button className="p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors text-center"
                onClick={() => setShowOrganizationJobs(true)}
              >
                <Briefcase className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <span className="text-sm font-medium text-gray-800">Student Jobs</span>
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="dashboard-card p-4" hidden>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Medal className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-gray-800">Emma Davis earned "Math Wizard" badge</p>
                  <p className="text-xs text-gray-500">5 minutes ago</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Trophy className="w-5 h-5 text-purple-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-gray-800">Sara Gomez completed weekly challenge</p>
                  <p className="text-xs text-gray-500">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-gray-800">Marcus Kim reached Level 4</p>
                  <p className="text-xs text-gray-500">1 day ago</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ThumbsUp className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm text-gray-800">Class average increased by 15%</p>
                  <p className="text-xs text-gray-500">2 days ago</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeacherStudent;
